import React from "react";
import { Plus, Trash2, Lock, Edit3 } from "lucide-react";
import { UnitToggle, TextField, NotesField, u } from "./_shared";
import { Field, TextInput } from "@/components/ReportPrimitives";
import {
  PUMP_TYPES, PUMP_TYPE_LABELS, DEFAULT_PUMP_PRESETS,
  computePumpDisplacement, pumpCalibration,
} from "@/lib/calc/pumpDisplacement";

// PumpProfileForm — geometry-first design.
//
// The user enters the *physical* pump spec:
//   Type · Liner Ø · Stroke length · Rod Ø (duplex DA only) · Efficiency %
// and the form computes Displacement automatically using the deterministic
// formulas in lib/calc/pumpDisplacement.js. Manual override remains available
// for unusual setups (e.g. calibrated stroke check after liner wear).
//
// Legacy pumps that only carry `displacement` continue to render — the user
// is prompted (helper text) to add geometry on next edit.

const _emptyPump = (i = 0) => ({
  label: `Pump #${i + 1}`,
  pumpType: "triplex_sa",
  liner: "", stroke: "", rodDia: "",
  efficiency: "97", cylinders: "3",
  displacement: "",
  measuredStrokes: "", measuredVolume: "",
  spm: "", slowPumpPressure: "",
});

function _round(v, digits) {
  if (v == null) return "";
  const m = Math.pow(10, digits);
  return String(Math.round(v * m) / m);
}

export default function PumpProfileForm({ value, onChange }) {
  const units = value.units || "IMP";
  const set = (patch) => onChange({ ...value, ...patch });
  const setPumps = (pumps) => set({ pumps });
  const updateRow = (i, patch) =>
    setPumps(value.pumps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addRow = () => setPumps([...(value.pumps || []), _emptyPump((value.pumps || []).length)]);
  const removeRow = (i) =>
    setPumps(value.pumps.length <= 1 ? value.pumps : value.pumps.filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-4" data-testid="pump-profile-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField value={value} onChange={onChange} field="label" label="Profile label"
                   placeholder="e.g. Rig 23 · Triplex Set A" testId="pump-label" />
        <TextField value={value} onChange={onChange} field="rig" label="Rig (optional)"
                   placeholder="e.g. Rig Alpha" testId="pump-rig" />
        <UnitToggle value={units} onChange={(v) => set({ units: v })} testId="pump-units" />
      </div>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Rig pumps
        </legend>
        <p className="text-[10px] text-[var(--rs-text-3)] mb-2 leading-snug">
          Displacement is derived from liner Ø, stroke, and efficiency. Click
          <strong className="text-[var(--rs-amber)]"> Override</strong> on the
          Displacement cell only if you need to enter a measured/calibrated value.
        </p>
        <div className="flex flex-col gap-3">
          {(value.pumps || []).map((p, i) => (
            <PumpRow key={i} index={i} pump={p} units={units}
                     onChange={(patch) => updateRow(i, patch)}
                     onRemove={() => removeRow(i)}
                     canRemove={value.pumps.length > 1} />
          ))}
        </div>
        <button type="button" onClick={addRow} data-testid="pump-add-row"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px]"
                style={{
                  background: "rgba(245,180,0,0.10)",
                  color: "var(--rs-amber)",
                  border: "1px solid rgba(245,180,0,0.35)",
                  minHeight: 32,
                }}>
          <Plus className="w-4 h-4" /> Add pump
        </button>
      </fieldset>

      <NotesField value={value} onChange={onChange} testId="pump-notes" />
    </div>
  );
}

function PumpRow({ index, pump, units, onChange, onRemove, canRemove }) {
  const isDuplex = pump.pumpType === "duplex_da";
  const dispAuto = pump._displacementManual !== true;
  const derived  = computePumpDisplacement(pump, units);
  const dispDisplay = dispAuto && derived != null
    ? _round(derived, units === "SI" ? 6 : 4)
    : (pump.displacement ?? "");

  const setType = (t) => {
    const preset = DEFAULT_PUMP_PRESETS[t] || {};
    const patch = { pumpType: t, ...preset };
    if (t === "triplex_sa") patch.rodDia = "";
    if (dispAuto) {
      const d = computePumpDisplacement({ ...pump, ...patch }, units);
      patch.displacement = d == null ? "" : _round(d, units === "SI" ? 6 : 4);
    }
    onChange(patch);
  };
  const setGeometry = (field, v) => {
    const patch = { [field]: v };
    if (dispAuto) {
      const d = computePumpDisplacement({ ...pump, ...patch }, units);
      patch.displacement = d == null ? "" : _round(d, units === "SI" ? 6 : 4);
    }
    onChange(patch);
  };
  const toggleDispAuto = () => {
    if (!dispAuto) {
      const d = computePumpDisplacement(pump, units);
      onChange({ _displacementManual: false, displacement: d == null ? "" : _round(d, units === "SI" ? 6 : 4) });
    } else {
      onChange({ _displacementManual: true });
    }
  };

  const linerUnit  = units === "SI" ? "mm" : "in";
  const strokeUnit = units === "SI" ? "mm" : "in";

  return (
    <div className="card-rs p-3" data-testid={`pump-row-${index}`}
         style={{ background: "rgba(22,24,29,0.4)" }}>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
        <div className="sm:col-span-3">
          <Field label="Label">
            <TextInput value={pump.label || ""} onChange={(v) => onChange({ label: v })}
                       testId={`pump-row-${index}-label`} />
          </Field>
        </div>
        <div className="sm:col-span-3">
          <Field label="Pump type">
            <select data-testid={`pump-row-${index}-type`} value={pump.pumpType || "triplex_sa"}
                    onChange={(e) => setType(e.target.value)}
                    className="input-rs text-[12px] w-full" style={{ height: 36 }}>
              {PUMP_TYPES.map((t) => <option key={t} value={t}>{PUMP_TYPE_LABELS[t]}</option>)}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={`Liner Ø (${linerUnit})`}>
            <TextInput type="number" value={pump.liner ?? ""} onChange={(v) => setGeometry("liner", v)}
                       testId={`pump-row-${index}-liner`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={`Stroke (${strokeUnit})`}>
            <TextInput type="number" value={pump.stroke ?? ""} onChange={(v) => setGeometry("stroke", v)}
                       testId={`pump-row-${index}-stroke`} />
          </Field>
        </div>
        {isDuplex && (
          <div className="sm:col-span-2">
            <Field label={`Rod Ø (${linerUnit})`} helper="Required for duplex DA.">
              <TextInput type="number" value={pump.rodDia ?? ""} onChange={(v) => setGeometry("rodDia", v)}
                         testId={`pump-row-${index}-rodDia`} />
            </Field>
          </div>
        )}
        <div className={isDuplex ? "sm:col-span-2" : "sm:col-span-2"}>
          <Field label="Efficiency %" helper="Triplex 95–98, Duplex 88–92.">
            <TextInput type="number" value={pump.efficiency ?? ""} onChange={(v) => setGeometry("efficiency", v)}
                       testId={`pump-row-${index}-eff`} />
          </Field>
        </div>

        <div className="sm:col-span-4">
          <Field
            label={`Displacement (${u.disp(units)})`}
            helper={dispAuto
              ? "Auto — derived from liner Ø, stroke, efficiency"
              : "Manual override active. Click Auto to recompute."}
          >
            <div className="flex items-stretch gap-2">
              <div className="flex-1">
                <TextInput type="number" value={dispDisplay}
                           onChange={(v) => onChange({ displacement: v })}
                           readOnly={dispAuto}
                           testId={`pump-row-${index}-disp`} />
              </div>
              <button type="button" onClick={toggleDispAuto}
                      data-testid={`pump-row-${index}-disp-toggle`}
                      title={dispAuto ? "Override" : "Reset to auto"}
                      className="text-[10px] px-2 rounded-md whitespace-nowrap inline-flex items-center gap-1"
                      style={{
                        minHeight: 36,
                        background: dispAuto ? "rgba(245,180,0,0.10)" : "rgba(22,24,29,0.6)",
                        color: dispAuto ? "var(--rs-amber)" : "var(--rs-text-2)",
                        border: `1px solid ${dispAuto ? "rgba(245,180,0,0.35)" : "var(--rs-border)"}`,
                      }}>
                {dispAuto ? (<><Lock className="w-3 h-3" /> Auto</>) : (<><Edit3 className="w-3 h-3" /> Override</>)}
              </button>
            </div>
          </Field>
        </div>
        <div className="sm:col-span-4">
          <Field label="Slow pump rate (SPM)">
            <TextInput type="number" value={pump.spm ?? ""} onChange={(v) => onChange({ spm: v })}
                       testId={`pump-row-${index}-spm`} />
          </Field>
        </div>
        <div className="sm:col-span-4">
          <Field label={`Slow pump pressure (${u.press(units)})`}>
            <TextInput type="number" value={pump.slowPumpPressure ?? ""}
                       onChange={(v) => onChange({ slowPumpPressure: v })}
                       testId={`pump-row-${index}-pressure`} />
          </Field>
        </div>
      </div>

      <CalibrationBlock pump={pump} units={units} index={index} onChange={onChange} />

      {canRemove && (
        <div className="flex justify-end mt-2">
          <button type="button" onClick={onRemove}
                  data-testid={`pump-row-${index}-remove`}
                  className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5]"
                  title="Remove pump">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}


function CalibrationBlock({ pump, units, index, onChange }) {
  const cal = pumpCalibration(pump, units);
  const status = cal?.status || null;
  const statusColor = status === "green" ? "#22C55E"
                    : status === "amber" ? "#F5B400"
                    : status === "red"   ? "#FCA5A5"
                    : "var(--rs-text-3)";
  const statusLabel = status === "green" ? "OK"
                    : status === "amber" ? "Marginal"
                    : status === "red"   ? "Re-calibrate"
                    : "Awaiting data";
  const volUnit = u.volume(units);
  return (
    <div className="mt-3 pt-3 border-t border-[var(--rs-border)]"
         data-testid={`pump-row-${index}-calibration`}>
      <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)]">
            Field calibration · optional
          </div>
          <p className="text-[10.5px] text-[var(--rs-text-3)] leading-snug mt-0.5">
            Verify pump output against an actual rig-floor stroke check.
            Informational — never blocks calculation.
          </p>
        </div>
        {(pump.measuredStrokes || pump.measuredVolume) && (
          <span data-testid={`pump-row-${index}-calibration-status`}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md"
                style={{
                  color: statusColor,
                  border: `1px solid ${statusColor}55`,
                  background: `${statusColor}15`,
                }}>
            {statusLabel}
            {cal?.ok && Number.isFinite(cal.variancePct)
              ? ` · ${cal.variancePct > 0 ? "+" : ""}${cal.variancePct.toFixed(2)} %`
              : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Field label="Measured strokes">
          <TextInput type="number" value={pump.measuredStrokes ?? ""}
                     onChange={(v) => onChange({ measuredStrokes: v })}
                     testId={`pump-row-${index}-measuredStrokes`} />
        </Field>
        <Field label={`Measured volume (${volUnit})`}>
          <TextInput type="number" value={pump.measuredVolume ?? ""}
                     onChange={(v) => onChange({ measuredVolume: v })}
                     testId={`pump-row-${index}-measuredVolume`} />
        </Field>
        <Field label={`Actual output (${u.disp(units)})`}
               helper="Measured volume ÷ measured strokes.">
          <div className="input-rs font-mono text-[12px] flex items-center px-3"
               data-testid={`pump-row-${index}-calibration-actual`}
               style={{ minHeight: 36, color: cal?.ok ? statusColor : "var(--rs-text-3)" }}>
            {cal?.ok ? Number(cal.actual.toFixed(units === "SI" ? 6 : 4)) : "—"}
          </div>
        </Field>
        <Field label="Variance vs derived"
               helper="Green ≤ 2 %, Amber 2–5 %, Red > 5 %.">
          <div className="input-rs font-mono text-[12px] flex items-center px-3"
               data-testid={`pump-row-${index}-calibration-variance`}
               style={{ minHeight: 36, color: statusColor }}>
            {cal?.ok && Number.isFinite(cal.variancePct)
              ? `${cal.variancePct > 0 ? "+" : ""}${cal.variancePct.toFixed(2)} %`
              : "—"}
          </div>
        </Field>
      </div>
    </div>
  );
}
